import {
  createAssetAssetPost,
  createAssetsAssetsPost,
  type AssetPayload,
} from "@phc/common/gen/preferencesGen"
import { useMutation } from "@tanstack/react-query"
import { useSnackbar } from "notistack"
import type { AssetInput } from "../../../../utils/preferencesApi"
import { useInvalidateAssetQueries } from "../../hooks/useAssetService"

export const useCreateAsset = ({
  hideToast,
  skipQueryInvalidation,
}: { hideToast?: boolean; skipQueryInvalidation?: boolean } = {}) => {
  const { invalidateAssetQueries } = useInvalidateAssetQueries()
  const { enqueueSnackbar } = useSnackbar()
  const successMessage = "Your new location has been saved."
  return useMutation({
    mutationFn: async (variables: { asset: AssetInput }) => {
      const response = await createAssetAssetPost({ asset: variables.asset })
      // Cast needed: the backend spec returns { additionalProperties: true } instead of a
      // proper response schema, so orval generates { [key: string]: unknown }.
      // The API actually returns { asset: AssetPayload }. Fix in the backend spec.
      return (response as { asset?: AssetPayload }).asset
    },
    onError: (err, req) => {
      console.error(err, req)
      enqueueSnackbar("Unable to create this new location.", {
        variant: "error",
      })
    },
    onSuccess: () => {
      if (!hideToast) {
        enqueueSnackbar(successMessage, {
          variant: "success",
        })
      }
    },
    onSettled: async () => {
      if (skipQueryInvalidation) return
      await invalidateAssetQueries()
    },
  })
}

export const useCreateAssets = () => {
  const { enqueueSnackbar } = useSnackbar()
  return useMutation({
    mutationFn: (variables: { assets: AssetInput[] }) =>
      createAssetsAssetsPost({ assets: variables.assets }),
    onError: err => {
      console.error(err)
      enqueueSnackbar("Unable to create these new locations.", {
        variant: "error",
      })
    },
  })
}
